import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { trpc } from '@/lib/trpc';
import { Loader2, BarChart3, Sparkles, ArrowRight } from 'lucide-react';

const getUtilizationColor = (utilization: number) => {
  if (utilization >= 80) return 'hsl(152, 69%, 40%)'; // green
  if (utilization >= 50) return 'hsl(38, 92%, 50%)'; // amber
  return 'hsl(0, 84%, 60%)'; // red
};

export function DepartureUtilizationChart() {
  const { t, i18n } = useTranslation('dashboard');
  const dateLocale = i18n.language === 'tr' ? 'tr-TR' : 'en-US';
  const { data, isLoading, error } = trpc.dashboard.departureUtilization.useQuery();

  if (isLoading) {
    return (
      <div className="luxury-card h-[380px] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[hsl(var(--muted-foreground))]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="luxury-card h-[380px] flex items-center justify-center">
        <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('chartError')}</p>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="luxury-card h-[380px] flex flex-col items-center justify-center">
        <div className="rounded-2xl bg-[hsl(var(--muted))] p-5">
          <BarChart3 className="h-10 w-10 text-[hsl(var(--muted-foreground))]" />
        </div>
        <p className="mt-4 font-display font-medium">{t('noDeparturesScheduled')}</p>
        <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))] text-center">
          {t('createPackagesWithDates')}
        </p>
      </div>
    );
  }

  const chartData = data.slice(0, 10).map((dep) => ({
    name: new Date(dep.departureDate).toLocaleDateString(dateLocale, {
      month: 'short',
      day: 'numeric',
    }),
    packageCode: dep.packageCode,
    packageName: dep.packageName,
    utilization: dep.utilization,
    booked: dep.booked,
    capacity: dep.capacity,
    isGuaranteed: dep.isGuaranteed,
  }));

  const avgUtilization =
    data.length > 0
      ? Math.round(data.reduce((sum, d) => sum + d.utilization, 0) / data.length)
      : 0;

  // soldOutCount available for future use: data.filter((d) => d.status === 'SOLD_OUT').length
  const guaranteedCount = data.filter((d) => d.isGuaranteed).length;

  return (
    <div className="luxury-card p-0 overflow-hidden">
      <div className="flex items-center justify-between border-b border-[hsl(var(--border))] px-6 py-4">
        <div>
          <h2 className="font-display text-lg font-semibold flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-blue-500" />
            {t('departureUtilization')}
          </h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('next30Days')}</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-center">
            <p className="font-display text-xl font-semibold">{avgUtilization}%</p>
            <p className="text-xs text-[hsl(var(--muted-foreground))]">{t('avgFillRate')}</p>
          </div>
          {guaranteedCount > 0 && (
            <div className="flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2 py-1">
              <Sparkles className="h-3 w-3 text-emerald-600" />
              <span className="text-xs font-medium text-emerald-700">
                {guaranteedCount} {t('guaranteed')}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="p-6">
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis
              dataKey="name"
              stroke="hsl(var(--muted-foreground))"
              fontSize={11}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              stroke="hsl(var(--muted-foreground))"
              fontSize={11}
              tickLine={false}
              axisLine={false}
              tickFormatter={(value) => `${value}%`}
              domain={[0, 100]}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
              }}
              formatter={(value: number, _name: string, props: any) => [
                <span key="value">
                  <strong>{value}%</strong> ({props.payload.booked}/{props.payload.capacity}{' '}
                  {t('seats')})
                </span>,
                props.payload.packageName,
              ]}
              labelFormatter={(label) => `${t('departure')}: ${label}`}
            />
            <Bar dataKey="utilization" radius={[4, 4, 0, 0]} maxBarSize={40}>
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getUtilizationColor(entry.utilization)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-6 px-6 pb-4">
        <div className="flex items-center gap-2">
          <div className="h-3 w-3 rounded-sm bg-emerald-500" />
          <span className="text-xs text-[hsl(var(--muted-foreground))]">80%+ {t('high')}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-3 w-3 rounded-sm bg-amber-500" />
          <span className="text-xs text-[hsl(var(--muted-foreground))]">50-79% {t('medium')}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-3 w-3 rounded-sm bg-red-500" />
          <span className="text-xs text-[hsl(var(--muted-foreground))]">&lt;50% {t('low')}</span>
        </div>
      </div>

      {data.length > 10 && (
        <div className="border-t border-[hsl(var(--border))] px-6 py-3">
          <Link
            to="/packages?tab=departures"
            className="flex items-center justify-center gap-2 text-sm font-medium text-[hsl(var(--gold))] hover:underline"
          >
            {t('viewAllDepartures')} ({data.length})
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      )}
    </div>
  );
}
